import React from 'react';
import { MapPin, SlidersHorizontal } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../ui/sheet';
import { LocationPermissionGate } from '../LocationPermissionGate';
import {
  CONDITION_GROUPS,
  PRICE_PRESETS,
  QUICK_CATEGORIES,
} from '../../containers/SearchPage/CustomSearchPage/useSearchPage';
import { NEAR_ME_STATUS, type NearMeStatus } from '../../hooks/useNearMe';

type ConditionGroup = (typeof CONDITION_GROUPS)[number];

export type QuickFiltersBindings = {
  activeFilterCount: number;
  totalItems: number | null;

  categories: Array<{ id: string; name: string }>;
  selectedCategoryId: string;
  onSelectCategory: (id: string) => void;
  onClearCategory: () => void;
  hideCategorySection?: boolean;

  conditionGroupLabel: string | null;
  onSelectConditionGroup: (group: ConditionGroup | null) => void;

  selectedPrice: string;
  onSelectPrice: (value: string) => void;

  hideNearMeSection?: boolean;
  pendingMiles: number | null;
  onSelectDistance: (miles: number) => void;
  nearMeStatus: NearMeStatus;
  isPersistentlyDeclined: boolean;
  onAllowLocation: () => void;
  onDismissLocation: () => void;
  isLocationActive: boolean;
  activeLocationLabel: string;
  onClearLocation: () => void;

  onReset: () => void;
};

export const QuickFiltersSheet = ({
  activeFilterCount,
  totalItems,
  categories,
  selectedCategoryId,
  onSelectCategory,
  onClearCategory,
  hideCategorySection,
  conditionGroupLabel,
  onSelectConditionGroup,
  selectedPrice,
  onSelectPrice,
  hideNearMeSection,
  pendingMiles,
  onSelectDistance,
  nearMeStatus,
  isPersistentlyDeclined,
  onAllowLocation,
  onDismissLocation,
  isLocationActive,
  activeLocationLabel,
  onClearLocation,
  onReset,
}: QuickFiltersBindings) => {
  const showNearMeGate =
    pendingMiles !== null &&
    nearMeStatus !== NEAR_ME_STATUS.Granted &&
    nearMeStatus !== NEAR_ME_STATUS.Asking &&
    !isPersistentlyDeclined;

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="h-11 px-4 font-heading uppercase tracking-wide"
          data-testid="button-open-search-filters"
        >
          <SlidersHorizontal className="mr-2 h-4 w-4" />
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-2 inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-primary px-1.5 text-xs text-primary-foreground">
              {activeFilterCount}
            </span>
          )}
        </Button>
      </SheetTrigger>

      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-heading uppercase text-2xl">Quick Filters</SheetTitle>
          <SheetDescription>
            Keep it simple. Pick a category, budget, and distance.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-8 space-y-6">
          {!hideCategorySection && (
            <div className="space-y-3">
              <Label className="text-sm font-semibold uppercase tracking-wide">Category</Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={!selectedCategoryId ? 'default' : 'outline'}
                  className="rounded-full font-heading uppercase tracking-wide"
                  onClick={onClearCategory}
                  data-testid="button-filter-category-any"
                >
                  Any
                </Button>
                {QUICK_CATEGORIES.map(id => {
                  const cat = categories.find(c => c.id === id);
                  const label = cat?.name ?? id;
                  return (
                    <Button
                      key={id}
                      type="button"
                      size="sm"
                      variant={selectedCategoryId === id ? 'default' : 'outline'}
                      className="rounded-full font-heading uppercase tracking-wide"
                      onClick={() => onSelectCategory(id)}
                      data-testid={`button-filter-category-${id}`}
                    >
                      {label}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}

          <div className={`space-y-3 ${!hideCategorySection ? 'border-t border-border pt-5' : ''}`}>
            <Label className="text-sm font-semibold uppercase tracking-wide">Condition</Label>
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant={!conditionGroupLabel ? 'default' : 'outline'}
                className="font-heading uppercase tracking-wide"
                onClick={() => onSelectConditionGroup(null)}
                data-testid="button-filter-condition-any"
              >
                Any
              </Button>
              {CONDITION_GROUPS.map(group => (
                <Button
                  key={group.label}
                  type="button"
                  variant={conditionGroupLabel === group.label ? 'default' : 'outline'}
                  className="font-heading uppercase tracking-wide"
                  onClick={() => onSelectConditionGroup(group)}
                  data-testid={group.testId}
                >
                  {group.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="space-y-3 border-t border-border pt-5">
            <Label className="text-sm font-semibold uppercase tracking-wide">Price</Label>
            <div className="grid grid-cols-2 gap-2">
              {PRICE_PRESETS.map(preset => (
                <Button
                  key={preset.label}
                  type="button"
                  variant={selectedPrice === preset.value ? 'default' : 'outline'}
                  className="font-heading uppercase tracking-wide"
                  onClick={() => onSelectPrice(preset.value)}
                  data-testid={`button-filter-price-${preset.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          </div>

          {!hideNearMeSection && (
          <div className="space-y-3 border-t border-border pt-5">
            <Label className="text-sm font-semibold uppercase tracking-wide">Near me</Label>
            <div className="grid grid-cols-3 gap-2">
              {[10, 25, 50].map(miles => (
                <Button
                  key={miles}
                  type="button"
                  variant={pendingMiles === miles ? 'default' : 'outline'}
                  className="font-heading uppercase tracking-wide"
                  disabled={nearMeStatus === NEAR_ME_STATUS.Asking}
                  onClick={() => onSelectDistance(miles)}
                  data-testid={`button-filter-distance-${miles}`}
                >
                  {miles} mi
                </Button>
              ))}
            </div>
            {isLocationActive ? (
              <div className="mt-3 flex items-center justify-between gap-2 text-sm">
                <span className="flex items-center gap-2 text-muted-foreground truncate">
                  <MapPin className="h-4 w-4 text-primary shrink-0" />
                  <span className="truncate">Near {activeLocationLabel}</span>
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onClearLocation}
                  className="font-heading uppercase tracking-wide shrink-0"
                  data-testid="button-filter-location-clear"
                >
                  Clear
                </Button>
              </div>
            ) : showNearMeGate ? (
              <LocationPermissionGate
                open
                variant="inline"
                status={nearMeStatus}
                onAllow={onAllowLocation}
                onDismiss={onDismissLocation}
              />
            ) : null}
          </div>
          )}

          <div className="border-t border-border pt-5 flex flex-col sm:flex-row gap-3">
            <Button
              type="button"
              variant="outline"
              className="flex-1 font-heading uppercase tracking-wide"
              onClick={onReset}
              data-testid="button-clear-all-search-filters"
            >
              Reset
            </Button>
            <Button
              type="button"
              className="flex-1 font-heading uppercase tracking-wide"
              data-testid="button-apply-search-filters"
            >
              {totalItems != null ? `Show ${totalItems} Results` : 'Apply Filters'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
